import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadDefinedGroupDetails, loadEOIConsentDetails } from '../../../store/directusService';
import {
    clearCurrentParticipantConnectToWork,
    loadParticipantConnectToWork,
    updateConnectToWork
} from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    DEFINED_GROUP_DETAILS_ERROR,
    EOI_CONSENT_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateConnectToWork';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const _MESSAGE = 'Participant connect to work has been updated';

const ConnectToWork = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        definedGroupIds: [],
        eoiConsentId: '',
        eoiCompletionDate: '',
        receivedFormCompletionHelp: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [ isDisabled, setIsDisabled ] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [keys, setKeys] = useState({
        definedGroup: '0',
        eoiConsent: '1'
    });
    const [preSelectedDefinedGroupIds, setPreSelectedDefinedGroupIds] = useState([]);
    const [preSelectedDefinedGroups, setPreSelectedDefinedGroups] = useState([]);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const definedGroupDetails = useSelector(
        (state) => state.entities.directusService.definedGroupDetails
    );
    const eoiConsentDetails = useSelector(
        (state) => state.entities.directusService.eoiConsentDetails
    );
    const participantConnectToWork = useSelector(
        (state) => state.entities.participantService.currentParticipantConnectToWork
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        definedGroupDetails?.length < 1 && dispatch(loadDefinedGroupDetails());
        eoiConsentDetails?.length < 1 && dispatch(loadEOIConsentDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setKeys(clearKeys(keys));
        if ('id' in currentParticipant)
            dispatch(loadParticipantConnectToWork(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant, roles]);

    useEffect(() => {
        if (definedGroupDetails?.length < 1) return;
        if(Object.keys(participantConnectToWork).length < 1) resetValues();
        else {
            const initial = {
                ...participantConnectToWork,
                definedGroupIds: participantConnectToWork.definedGroupIds || [],
                eoiConsentId: participantConnectToWork.eoiConsentId || '',
                eoiCompletionDate: participantConnectToWork.eoiCompletionDate || '',
            };
            setNewEntry(initial);
            setInitialStateOnEntry(initial);

            if (definedGroupDetails?.length && participantConnectToWork?.definedGroupIds?.length) {
                const preSelects = definedGroupDetails.filter((el) =>
                    participantConnectToWork.definedGroupIds.includes(el.id)
                );
                setPreSelectedDefinedGroups(preSelects);
                setPreSelectedDefinedGroupIds(preSelects.map((el) => el.id));
            }
        }

    }, [participantConnectToWork, definedGroupDetails]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE)
            setIsUpdateButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (definedGroupDetails?.length < 1) return DEFINED_GROUP_DETAILS_ERROR;
        if (eoiConsentDetails?.length < 1) return EOI_CONSENT_DETAILS_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const resetValues = () => {
        setNewEntry(initialState);
        setInitialStateOnEntry(initialState);
        setPreSelectedDefinedGroupIds([]);
        setPreSelectedDefinedGroups([]);
    };

    const cleanUp = () => {
        dispatch(clearCurrentParticipantConnectToWork());
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        if (value == null) value = '';
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id
        };
        dispatch(updateConnectToWork(payload, _MESSAGE));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={form.formWrapper}>
            <form
                onSubmit={onSubmit}
                data-testid="form_start_connect_to_work"
                className={form.form}>
                <h2 className={app.sectionHeading}>EOI Details</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <RadioButtons
                            id="receivedFormCompletionHelp"
                            label={
                                'Have you received help from an advocate in completing this Form?'
                            }
                            disabled={isDisabled}
                            value={newEntry.receivedFormCompletionHelp || false}
                            onChange={(option) => onUpdate('receivedFormCompletionHelp', option)}
                        />
                        <SingleSelect
                            id="eoiConsentId"
                            key={keys.eoiConsent}
                            label="EOI Consent"
                            placeholder="Select EOI Consent..."
                            disabled={isDisabled}
                            menuItems={eoiConsentDetails || []}
                            selectedId={newEntry.eoiConsentId || ''}
                            selected={getObjFromId(eoiConsentDetails, newEntry.eoiConsentId)}
                            onChange={(chosenId) => onUpdate('eoiConsentId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="definedGroupIds"
                            key={keys.definedGroup}
                            label="Defined Group"
                            placeholder="Select Defined Group..."
                            disabled={isDisabled}
                            menuItems={definedGroupDetails}
                            preSelectedIds={preSelectedDefinedGroupIds}
                            preSelects={preSelectedDefinedGroups}
                            onChange={(chosenIds) => onUpdate('definedGroupIds', chosenIds)}
                        />
                        <DateSelect
                            id="eoiCompletionDate"
                            label="EOI Completion Date"
                            disabled={isDisabled}
                            value={newEntry.eoiCompletionDate}
                            isDefault={false}
                            error={errors.eoiCompletionDate}
                            onDateChange={(date) => onUpdate('eoiCompletionDate', date)}
                        />
                    </div>
                </div>

                <Button
                    id="connectToWorkUpdateButton"
                    disabled={isDisabled || isUpdateButtonDisabled}
                    content="Update Connect To Work"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

export default ConnectToWork;
